import React from "react";
import { Text, TextField } from "@origin-digital/ods-core";
import styled from "@emotion/styled";
import { useFormikContext } from "formik";
import { BulkDuplicateFormValues } from "./types";

const FieldItemBox = styled.div(() => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "8px 12px",

  "& .field-label": {
    display: "none !important",
  },

  "& .field-comma": {
    display: "none !important",
  },

  "& input": {
    padding: "20px 8px !important",
    width: "100% !important",
    boxSizing: "border-box" as const,
  },

  "@media (min-width: 600px)": {
    width: "50%",

    "&:nth-of-type(odd):last-child": {
      width: "100%",
    },
  },

  "@media (min-width: 992px)": {
    width: "auto",
    padding: "0 12px",

    "& .field-label": {
      display: "block !important",
    },

    "& .field-comma": {
      display: "block !important",
    },

    "& input": {
      width: "80px !important",
    },

    "& .MuiTextField-root": {
      width: "80px !important",
    },

    "& .MuiOutlinedInput-root": {
      width: "80px !important",
    },

    "& .MuiInputAdornment-root": {
      display: "none !important",
    },
  },
}));

interface BulkDuplicateTextFieldProps {
  id: string;
  label: string;
  fieldLabel?: string;
  value: string;
  hasError?: boolean;
  readOnly?: boolean;
  showComma?: boolean;
  premiseIndex: number;
  fieldName: string;
  maxLength?: number;
}

const getReadOnlySx = () => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent !important",
    cursor: "default",
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: "transparent !important",
  },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D0D0D0 !important",
    borderWidth: "1px !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D0D0D0 !important",
    borderWidth: "1px !important",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D0D0D0 !important",
    borderWidth: "1px !important",
  },
});

export const BulkDuplicateTextField: React.FC<BulkDuplicateTextFieldProps> = ({
  id,
  label,
  fieldLabel,
  value,
  hasError = false,
  readOnly = false,
  showComma = true,
  premiseIndex,
  fieldName,
  maxLength,
}) => {
  const { setFieldValue } = useFormikContext<BulkDuplicateFormValues>();
  const lengthProp = maxLength ? { maxLength } : {};
  const muiProps = readOnly
    ? {
        inputProps: {
          "aria-label": label,
          readOnly: true,
          ...lengthProp,
        },
        sx: getReadOnlySx(),
      }
    : {
        inputProps: {
          "aria-label": label,
          ...lengthProp,
        },
      };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFieldValue(`premises.${premiseIndex}.${fieldName}`, newValue);
  };

  return (
    <FieldItemBox>
      {fieldLabel && <Text className="field-label">{fieldLabel}</Text>}
      <TextField
        id={id}
        size="small"
        label={label}
        muiProps={muiProps}
        value={value}
        onChange={handleChange}
        hideHelperTextSpace
        error={hasError}
      />
      {showComma && <Text className="field-comma">,</Text>}
    </FieldItemBox>
  );
};
